import { useEffect, useState } from "react";
import {
  Excalidraw,
  convertToExcalidrawElements,
  MainMenu,
} from "@excalidraw/excalidraw";
import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";
import { graphToExcalidraw } from "../src/graphToExcalidraw";
import { DEFAULT_FONT_SIZE } from "../src/constants";
import type { MermaidData } from "./";
import { ensureExcalidrawFontsLoaded } from "./loadExcalidrawFonts";
import { useSagamudaMermaidModal } from "./useSagamudaMermaidModal";
interface ExcalidrawWrapperProps {
  mermaidDefinition: MermaidData["definition"];
  mermaidOutput: MermaidData["output"];
  theme: "light" | "dark";
  apiRef?: React.MutableRefObject<ExcalidrawImperativeAPI | null>;
  onChange?: (elements: any[], appState: any) => void;
  initialAppState?: any;
}

const ExcalidrawWrapper = ({
  mermaidDefinition,
  mermaidOutput,
  theme,
  apiRef,
  onChange,
  initialAppState,
}: ExcalidrawWrapperProps) => {
  const [readyExcalidrawAPI, setReadyExcalidrawAPI] =
    useState<ExcalidrawImperativeAPI | null>(null);
  const [librariesLoaded, setLibrariesLoaded] = useState(false);

  // Hook to override Excalidraw's built-in Mermaid to Excalidraw modal
  useSagamudaMermaidModal(readyExcalidrawAPI);

  useEffect(() => {
    // Force browser to load the font globally for the engine
    const font = new FontFace(
      "Shantell Sans",
      "url(https://fonts.gstatic.com/s/shantellsans/v13/FeUaS0pCoLIo-lcdY7kjvNoQqWVWB0qWpl29ajppTuUTu_kJKmHesPOL-maYi4xZeHCNQ09eBlmv2QcUzJ39-rAISYR8S2i2.ttf)"
    );
    font.load().then((loadedFont) => {
      document.fonts.add(loadedFont);
    }).catch(e => console.warn("FontFace injection failed", e));
  }, []);

  useEffect(() => {
    let isCancelled = false;

    if (!readyExcalidrawAPI || readyExcalidrawAPI.isDestroyed) {
      return undefined;
    }

    if (mermaidDefinition === "" || mermaidOutput === null) {
      readyExcalidrawAPI.resetScene();
      return undefined;
    }

    void (async () => {
      try {
        await ensureExcalidrawFontsLoaded();
        if (isCancelled || readyExcalidrawAPI.isDestroyed) {
          return;
        }

        let elements: any[] = [];
        let files: any = null;

        if (mermaidOutput?.type === "markdown") {
          elements = mermaidOutput.elements;
          console.log("MARKDOWN ELEMENTS TO EXCALIDRAW:", elements.map(el => ({ id: el.id, type: el.type, groupIds: el.groupIds })));
        } else {
          const result = graphToExcalidraw(mermaidOutput, {
            fontSize: DEFAULT_FONT_SIZE,
          });
          elements = convertToExcalidrawElements(result.elements);
          files = result.files;
        }

        readyExcalidrawAPI.updateScene({
          elements,
        });

        if (files) {
          readyExcalidrawAPI.addFiles(Object.values(files));
        }
      } catch (e) {
        console.error("Critical error during Excalidraw scene update:", e);
      }
    })();

    return () => {
      isCancelled = true;
    };
  }, [mermaidDefinition, mermaidOutput, readyExcalidrawAPI]);

  useEffect(() => {
    if (readyExcalidrawAPI && !librariesLoaded) {
      const loadLibraries = async () => {
        // Wait a bit for the API to be ready for library updates
        await new Promise((resolve) => setTimeout(resolve, 500));
        
        const libs = [
          "libraries/software-architecture.excalidrawlib",
          "libraries/cloud.excalidrawlib",
          "libraries/aws-architecture-icons.excalidrawlib",
        ];

        for (const libUrl of libs) {
          try {
            const response = await fetch(`${window.location.origin}/${libUrl}`);
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            
            const data = await response.json();
            const rawItems = data.libraryItems || data.library || [];
            
            let libraryItems = [];
            if (Array.isArray(rawItems)) {
              if (rawItems.length > 0 && Array.isArray(rawItems[0])) {
                libraryItems = rawItems.map((elements) => ({
                  status: "published",
                  elements,
                }));
              } else {
                libraryItems = rawItems;
              }
            }

            if (libraryItems.length > 0) {
              readyExcalidrawAPI.updateLibrary({
                libraryItems,
                merge: true,
                openLibraryMenu: false,
              });
            }
          } catch (e) {
            console.error("Failed to load library", libUrl, e);
          }
        }
        setLibrariesLoaded(true);
      };
      loadLibraries();
    }
  }, [readyExcalidrawAPI, librariesLoaded]);

  // Bypass missing type definition for 'fonts' prop in alpha version
  const excalidrawProps: any = {
    theme,
    fonts: [
      {
        uri: "https://fonts.gstatic.com/s/shantellsans/v13/FeUaS0pCoLIo-lcdY7kjvNoQqWVWB0qWpl29ajppTuUTu_kJKmHesPOL-maYi4xZeHCNQ09eBlmv2QcUzJ39-rAISYR8S2i2.ttf",
        id: "shantell-sans",
        label: "Shantell Sans",
        family: '"Shantell Sans", sans-serif',
      }
    ],
    UIOptions: {
      canvasActions: {
        clearCanvas: true,
        loadScene: true,
        saveToActiveFile: true,
        toggleTheme: false,
        export: {
          saveFileToDisk: true,
        },
      },
    },
    aiEnabled: false,
    initialData: {
      appState: {
        viewBackgroundColor: "#fafafa",
        currentItemFontFamily: 1,
        openSidebar: { name: "pages" },
        ...initialAppState,
      },
    },
    onExcalidrawAPI: (api: any) => {
      if (apiRef) {
        apiRef.current = api;
      }
    },
    onInitialize: (api: any) => {
      setReadyExcalidrawAPI(api);
    },
    onChange,
    libraryReturnUrl: window.location.origin,
    onUnmount: () => {
      setReadyExcalidrawAPI(null);
      if (apiRef) {
        apiRef.current = null;
      }
    },
  };

  return (
    <div className="excalidraw-wrapper">
      <Excalidraw {...excalidrawProps}>
        <MainMenu>
          <MainMenu.DefaultItems.LoadScene />
          <MainMenu.DefaultItems.SaveToActiveFile />
          <MainMenu.DefaultItems.Export />
          <MainMenu.DefaultItems.SaveAsImage />
          <MainMenu.DefaultItems.ClearCanvas />
          <MainMenu.Separator />
          <MainMenu.DefaultItems.ToggleTheme />
          <MainMenu.DefaultItems.ChangeCanvasBackground />
        </MainMenu>
      </Excalidraw>
    </div>
  );
};

export default ExcalidrawWrapper;
